import React, { useState, useEffect, useCallback, useRef } from 'react'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import 'bootstrap-icons/font/bootstrap-icons.css'
import L, { FeatureGroup, LatLngBounds, LatLngTuple } from 'leaflet'
import 'leaflet-draw'
import * as GeoTIFF from 'geotiff'
import { useRunRealtimeTiffInferenceMutation } from '../../../services/modelsApi'
import { useAppSelector } from '../../../store/hooks'

import styled from 'styled-components'
import { RealTimeInferenceResult } from '../../../types/prediction'
import { PredictionMetadata } from '../../../types/models/prediction'
import {
  useGetWmsCapabilitiesMutation,
  useGetWmsMapMutation,
  WmsCapabilitiesResponse,
  WmsMapResponse,
} from '../../../services/experimentalApi'
import { selectIsAuthenticated } from '../../../store/slices/authSlice'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import ErrorDialog from '../../dialogs/ErrorDialog'
import {
  saveLayer,
  loadLayers,
  deleteLayer,
  processImageForStorage,
  createThumbnail,
} from '../../../utils/layerStorage'
import { fileStorage } from '../../../utils/fileStorage'

// Import map components and types with renamed imports to avoid conflicts
import {
  MapContainer as MapContainerComponent,
  InferenceToolMapPanel,
  WmsPanel,
  DrawPanel,
  WmsActionButton,
  PredictionsPanel,
  MapToolbar,
} from './map'
import type { MapTool } from './map/MapToolbar'
import {
  calculateGSD as utilCalculateGSD,
  createDebugOverlay as utilCreateDebugOverlay,
  drawPredictions as utilDrawPredictions,
} from './map/mapUtils'
import { ResolutionPresets, LayerInfo } from './map/types'

const ContentContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
`

interface MapComponentProps {
  allowDragAndDrop?: boolean
  showLayersPanel?: boolean
}

const MapComponent: React.FC<MapComponentProps> = ({
  allowDragAndDrop = true,
  showLayersPanel = true,
}) => {
  const [bboxBounds, setBboxBounds] = useState<LatLngBounds | null>(null)
  const [drawnItems, setDrawnItems] = useState<FeatureGroup<any> | null>(null)

  const [map, setMap] = useState<L.Map | null>(null)
  const mapInitializedRef = useRef<boolean>(false)
  const [imageOverlays, setImageOverlays] = useState<L.ImageOverlay[]>([])
  const [layers, setLayers] = useState<LayerInfo[]>([])

  const [isDragging, setIsDragging] = useState(false)
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [isLayersPanelVisible, setIsLayersPanelVisible] = useState<boolean>(showLayersPanel)

  // WMS API call states
  const [connectId, setConnectId] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  // Define preset resolutions
  const PRESET_RESOLUTIONS: ResolutionPresets = {
    'HD (1920x1080)': { width: 1920, height: 1080 },
    'Full HD (2560x1440)': { width: 2560, height: 1440 },
    '4K (3840x2160)': { width: 3840, height: 2160 },
    '8K (7680x4320)': { width: 7680, height: 4320 },
    'Max (10000x10000)': { width: 10000, height: 10000 },
  } as const

  const [selectedResolution, setSelectedResolution] =
    useState<keyof typeof PRESET_RESOLUTIONS>('4K (3840x2160)')
  const { width: wmsImageWidth, height: wmsImageHeight } = PRESET_RESOLUTIONS[selectedResolution]

  // Check if user is authenticated
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  // Get the hooks from the experimentalApi
  const [fetchWMSCapabilities, { isLoading: isLoadingCapabilities }] =
    useGetWmsCapabilitiesMutation()

  const [fetchWMSMap, { isLoading: isLoadingMap }] = useGetWmsMapMutation()

  // Add drawControl to state declarations at the top
  const [drawControl, setDrawControl] = useState<L.Draw.Rectangle | null>(null)

  // New state for WMS authentication
  const [isWmsAuthenticated, setIsWmsAuthenticated] = useState<boolean>(false)

  // New state for showing the GEGD panel and draw panel
  const [showGEGDPanel, setShowGEGDPanel] = useState<boolean>(false)
  const [showDrawPanel, setShowDrawPanel] = useState<boolean>(false)
  const [showPredictionsPanel, setShowPredictionsPanel] = useState<boolean>(false)

  // New state for inference
  const [isInferencing, setIsInferencing] = useState<string | null>(null)
  const selectedModelId = useAppSelector(selectSelectedModelId) || null

  // At the component level, near other hooks
  const [runRealtimeTiffInference] = useRunRealtimeTiffInferenceMutation()

  // State for the active map tool
  const [activeTool, setActiveTool] = useState<MapTool>(null)
  const [isMapDragOver, setIsMapDragOver] = useState(false)
  const [isProcessingFile, setIsProcessingFile] = useState(false)

  // Handler for toolbar tool selection
  const handleToolSelect = useCallback(
    (tool: MapTool) => {
      setActiveTool(tool)
      // Automatically open panel when inference tool is selected
      if (tool === 'inference') {
        setIsLayersPanelVisible(true)

        // Restore layers to map if they exist
        if (map && layers.length > 0) {
          layers.forEach((layer) => {
            // Re-add overlay to map if it's not already there
            if (layer.overlay && !map.hasLayer(layer.overlay)) {
              layer.overlay.addTo(map)
            }

            // Re-add prediction rectangles if they exist
            if (layer.predictionRectangles && layer.predictionRectangles.length > 0) {
              layer.predictionRectangles.forEach((rect) => {
                if (!map.hasLayer(rect)) {
                  rect.addTo(map)
                }
              })
            }
          })

          // Update imageOverlays state to include visible overlays
          const visibleOverlays = layers
            .filter((layer) => layer.isVisible)
            .map((layer) => layer.overlay)
          setImageOverlays(visibleOverlays)

          // Check if any layers have predictions and restore predictions panel if needed
          const hasLayersWithPredictions = layers.some(
            (layer) => layer.predictions && layer.predictions.length > 0
          )
          if (hasLayersWithPredictions) {
            setShowPredictionsPanel(true)
          }
        }
      } else {
        // Close all inference-related panels when other tools are selected or inference is turned off
        setIsLayersPanelVisible(false)
        setShowPredictionsPanel(false)

        // Hide layers from map but keep data for restoration
        if (map && layers.length > 0) {
          layers.forEach((layer) => {
            // Remove overlay from map (but don't destroy it)
            if (layer.overlay && map.hasLayer(layer.overlay)) {
              map.removeLayer(layer.overlay)
            }

            // Remove prediction rectangles from map (but don't destroy them)
            if (layer.predictionRectangles && layer.predictionRectangles.length > 0) {
              layer.predictionRectangles.forEach((rect) => {
                if (map.hasLayer(rect)) {
                  map.removeLayer(rect)
                }
              })
            }
          })

          // Clear imageOverlays display state but keep layer data
          setImageOverlays([])

          // Clear processing states
          setIsProcessingFile(false)
          setIsInferencing(null)
        }
      }
    },
    [map, layers]
  )

  // Create a dedicated toggle function for the layers panel
  const toggleLayersPanel = useCallback(() => {
    setIsLayersPanelVisible((prevState) => !prevState)
    console.log('Toggling layers panel, new state will be:', !isLayersPanelVisible)
  }, [isLayersPanelVisible])

  // Toggle function for the predictions panel
  const togglePredictionsPanel = useCallback(() => {
    setShowPredictionsPanel((prevState) => !prevState)
    console.log('Toggling predictions panel, new state will be:', !showPredictionsPanel)
  }, [showPredictionsPanel])

  // Update the useEffect for map initialization
  useEffect(() => {
    // Create map container element if it doesn't exist
    const mapContainer = document.getElementById('map')

    if (!mapContainer || mapInitializedRef.current) {
      return
    }

    // Set initialized flag to prevent re-initialization
    mapInitializedRef.current = true

    // Wait for the container to be properly mounted
    setTimeout(() => {
      try {
        const northSouthBounds: LatLngTuple[] = [
          [-85, -Infinity],
          [85, Infinity],
        ]

        // Initialize map with safer options
        const mapInstance = L.map('map', {
          maxBounds: northSouthBounds,
          maxBoundsViscosity: 1.0,
          minZoom: 3,
          maxZoom: 18,
          zoomControl: false,
          zoomSnap: 0.5,
          wheelPxPerZoomLevel: 120,
          fadeAnimation: true,
          zoomAnimation: true,
        })

        // Add zoom control to the top-left corner
        L.control
          .zoom({
            position: 'topleft',
          })
          .addTo(mapInstance)

        // Add scale control to the bottom-left corner
        L.control
          .scale({
            imperial: true,
            metric: true,
            position: 'topright',
          })
          .addTo(mapInstance)

        // Add base tile layer
        L.tileLayer(
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          {
            attribution:
              '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
            maxZoom: 19,
            crossOrigin: true,
          }
        ).addTo(mapInstance)

        // Create feature group for drawn items
        const drawnItemsLayer = new L.FeatureGroup()
        setDrawnItems(drawnItemsLayer)
        mapInstance.addLayer(drawnItemsLayer)

        // Set initial view after a short delay to ensure container is ready
        setTimeout(() => {
          mapInstance.setView([39.8283, -98.5795], 3)
        }, 100)

        // Store map instance in state
        setMap(mapInstance)

        // Setup drawn item events
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mapInstance.on(L.Draw.Event.CREATED, (e: any) => {
          const layer = e.layer

          if (drawnItemsLayer) {
            drawnItemsLayer.clearLayers()
            drawnItemsLayer.addLayer(layer)

            // Set the bounding box (only for layers that have getBounds method)
            if ('getBounds' in layer && typeof layer.getBounds === 'function') {
              setBboxBounds(layer.getBounds())
            }
          }
        })
      } catch (error) {
        console.error('Error initializing map:', error)
        mapInitializedRef.current = false // Reset flag on error
      }
    }, 0)

    // Cleanup function
    return () => {
      if (map) {
        map.remove()
        setMap(null)
        mapInitializedRef.current = false // Reset flag on cleanup
      }
    }
  })

  // Replace this entire useEffect
  useEffect(() => {
    if (!map) return

    const loadMapLayers = async () => {
      try {
        const layers = await loadLayers(map)
        if (layers.length > 0) {
          setLayers(layers)
          setImageOverlays(layers.map((layer) => layer.overlay))

          const visibleLayer = layers.find((layer) => layer.isVisible)
          if (visibleLayer && map) {
            map.fitBounds(visibleLayer.bounds, {
              padding: [50, 50],
              animate: true,
              duration: 0.5,
            })
          }
        }
      } catch (error) {
        console.error('Error in layer loading process:', error)
      }
    }

    loadMapLayers()
  }, [map])

  // Load saved WMS credentials on mount
  useEffect(() => {
    const savedConnectId = localStorage.getItem('wms_connect_id')
    const savedUsername = localStorage.getItem('wms_username')
    const savedPassword = localStorage.getItem('wms_password')

    if (savedConnectId) setConnectId(savedConnectId)
    if (savedUsername) setUsername(savedUsername)
    if (savedPassword) setPassword(savedPassword)
  }, [])

  // Save WMS credentials when they change
  useEffect(() => {
    if (connectId) localStorage.setItem('wms_connect_id', connectId)
    if (username) localStorage.setItem('wms_username', username)
    if (password) localStorage.setItem('wms_password', password)
  }, [connectId, username, password])

  // Save WMS auth state
  useEffect(() => {
    localStorage.setItem('wms_authenticated', isWmsAuthenticated.toString())
  }, [isWmsAuthenticated])

  // Restore WMS auth state
  useEffect(() => {
    const savedAuthState = localStorage.getItem('wms_authenticated')
    if (savedAuthState === 'true' && connectId && username && password) {
      // Try to authenticate again with saved credentials
      fetchWMSCapabilities({
        connect_id: connectId,
        username: username,
        password: password,
      })
        .unwrap()
        .then(() => {
          setIsWmsAuthenticated(true)
        })
        .catch(() => {
          setIsWmsAuthenticated(false)
        })
    }
  }, [fetchWMSCapabilities, connectId, username, password])

  // Fix Leaflet default icon path issue
  useEffect(() => {
    // Fix Leaflet's default icon path issues
    delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    })
  }, [])

  // Update toggleLayerVisibility
  const toggleLayerVisibility = (layerId: string) => {
    setLayers((prev) => {
      const updatedLayers = prev.map((layer) => {
        if (layer.id === layerId) {
          // Create a new overlay object with the same properties to prevent stale references
          const newVisibility = !layer.isVisible

          if (layer.isVisible) {
            // If currently visible, remove from map
            layer.overlay.remove()
            // Also hide prediction rectangles
            if (layer.predictionRectangles) {
              layer.predictionRectangles.forEach((rect) => rect.remove())
            }
          } else {
            // If currently hidden, add to map
            if (map) {
              layer.overlay.addTo(map)
              // Also show prediction rectangles
              if (layer.predictionRectangles) {
                layer.predictionRectangles.forEach((rect) => rect.addTo(map))
              }
            }
          }

          // Create updated layer with new visibility
          const updatedLayer = { ...layer, isVisible: newVisibility }

          // Save the updated layer
          saveLayer(updatedLayer)

          return updatedLayer
        }
        return layer
      })

      // Update imageOverlays state to match the current visible layers
      setImageOverlays(
        updatedLayers.filter((layer) => layer.isVisible).map((layer) => layer.overlay)
      )

      return updatedLayers
    })
  }

  // Update removeLayer to handle async operations
  const removeLayer = (layerId: string) => {
    console.log(`Removing layer with ID: ${layerId}`)

    setLayers((prev) => {
      const layerToRemove = prev.find((layer) => layer.id === layerId)

      if (layerToRemove) {
        console.log('Found layer to remove:', layerToRemove.id)

        try {
          // Explicitly remove from the map
          if (map && layerToRemove.overlay) {
            console.log('Removing overlay from map')
            map.removeLayer(layerToRemove.overlay)
            layerToRemove.overlay.remove()
          }

          // Remove prediction rectangles if they exist
          if (layerToRemove.predictionRectangles && layerToRemove.predictionRectangles.length > 0) {
            console.log(
              `Removing ${layerToRemove.predictionRectangles.length} prediction rectangles`
            )
            layerToRemove.predictionRectangles.forEach((rect) => {
              if (map) {
                map.removeLayer(rect)
              }
              rect.remove()
            })
          }

          // Delete from storage - async but we don't need to wait
          console.log('Deleting layer from storage')
          deleteLayer(layerId).catch((err: Error) =>
            console.warn('Error deleting from storage:', err)
          )

          // Also remove from file storage if it exists
          console.log('Removing file from storage')
          fileStorage.removeFile(layerId)
        } catch (error) {
          console.error(`Error while removing layer ${layerId}:`, error)
        }
      } else {
        console.warn(`Layer with ID ${layerId} not found for removal`)
      }

      // Update imageOverlays state to remove the specific overlay
      if (layerToRemove && layerToRemove.overlay) {
        const overlayToRemove = layerToRemove.overlay
        setTimeout(() => {
          setImageOverlays((prevOverlays) => {
            const updatedOverlays = prevOverlays.filter((overlay) => overlay !== overlayToRemove)
            console.log(
              `Updated imageOverlays from ${prevOverlays.length} to ${updatedOverlays.length}`
            )
            return updatedOverlays
          })
        }, 0)
      }

      // Return filtered layers
      const remainingLayers = prev.filter((layer) => layer.id !== layerId)
      console.log(`Reduced layers from ${prev.length} to ${remainingLayers.length}`)

      // Close predictions panel if no layers remain
      if (remainingLayers.length === 0) {
        setShowPredictionsPanel(false)
      }

      return remainingLayers
    })

    // Force a refresh of all visible layers on the map
    setTimeout(() => {
      console.log('Refreshing all layers on map')
      setLayers((prevLayers) => {
        // Return the same layers to avoid re-rendering but force refresh the map components
        return [...prevLayers]
      })
    }, 100)
  }

  // Update handleDrop to check allowDragAndDrop and active tool
  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      if (!allowDragAndDrop || activeTool !== 'inference') return
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(false)
      setIsMapDragOver(false)

      const files = event.dataTransfer.files
      if (!files || files.length === 0) {
        alert('No files were dropped')
        return
      }

      // For inference mode, only process the first file and run inference automatically
      if (activeTool === 'inference') {
        if (files.length > 1) {
          setErrorMessage('Please drop only one TIFF file at a time for inference.')
          setShowError(true)
          return
        }

        const file = files[0]
        if (!file.name.toLowerCase().match(/\.(tiff?|tif)$/)) {
          setErrorMessage('Please drop a valid TIFF file (.tif or .tiff)')
          setShowError(true)
          return
        }

        // Show loading indicator immediately
        setIsProcessingFile(true)
      }

      // Create an array to store the new layers
      const newLayers: LayerInfo[] = []
      const tempLayerIds: string[] = []

      // First, create placeholder layers for all files
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const tempLayerId = `processing-${Date.now()}-${i}`
        tempLayerIds.push(tempLayerId)

        // Create a temporary placeholder layer that shows as "processing"
        const placeholderLayer: LayerInfo = {
          id: tempLayerId,
          bounds: new L.LatLngBounds([0, 0], [0, 0]), // Temporary bounds
          overlay: new L.ImageOverlay('', new L.LatLngBounds([0, 0], [0, 0])),
          isVisible: false,
          isProcessing: true, // Mark as processing
          metadata: {
            width: 0,
            height: 0,
            samplesPerPixel: 0,
            bitsPerSample: [0],
            photometric: 0,
          },
          originalFile: file, // Keep reference to file for display
        }

        // Add placeholder to layers
        setLayers((prev) => [...prev, placeholderLayer])
      }

      // Process each file sequentially
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const processingId = tempLayerIds[i]

        try {
          const arrayBuffer = await file.arrayBuffer()
          const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer)
          const image = await tiff.getImage()

          // Get image metadata
          const fileDirectory = image.getFileDirectory()
          const bbox = image.getBoundingBox()
          const geoKeys = image.getGeoKeys()

          // Calculate GSD using the utility function
          let gsdInfo = { gsd: 0, details: {} }
          if (bbox) {
            gsdInfo = utilCalculateGSD(image, bbox, geoKeys)
          }

          // Get the data from all bands
          const data = (await image.readRasters()) as Float32Array[]
          const canvas = document.createElement('canvas')
          canvas.width = image.getWidth()
          canvas.height = image.getHeight()
          const ctx = canvas.getContext('2d')

          if (!ctx) {
            throw new Error('Could not get canvas context')
          }

          // Create ImageData from the raster data
          const imageData = ctx.createImageData(canvas.width, canvas.height)
          const samples = image.getSamplesPerPixel()

          // Check if this is a standard RGB image
          const isRGB = fileDirectory.PhotometricInterpretation === 2 // 2 = RGB
          const isYCbCr = fileDirectory.PhotometricInterpretation === 6 // 6 = YCbCr

          if (isRGB && samples >= 3) {
            // For RGB images, use the values directly
            for (let i = 0; i < data[0].length; i++) {
              for (let band = 0; band < 3; band++) {
                imageData.data[i * 4 + band] = Math.min(255, Math.max(0, data[band][i]))
              }
              imageData.data[i * 4 + 3] = 255 // Alpha channel
            }
          } else if (isYCbCr && samples >= 3) {
            // Convert YCbCr to RGB
            for (let i = 0; i < data[0].length; i++) {
              const Y = data[0][i]
              const Cb = data[1][i]
              const Cr = data[2][i]

              // YCbCr to RGB conversion
              const R = Y + 1.402 * (Cr - 128)
              const G = Y - 0.344136 * (Cb - 128) - 0.714136 * (Cr - 128)
              const B = Y + 1.772 * (Cb - 128)

              // Clamp values to 0-255 range
              imageData.data[i * 4] = Math.min(255, Math.max(0, R)) // R
              imageData.data[i * 4 + 1] = Math.min(255, Math.max(0, G)) // G
              imageData.data[i * 4 + 2] = Math.min(255, Math.max(0, B)) // B
              imageData.data[i * 4 + 3] = 255 // A
            }
          } else {
            // Convert other formats - code unchanged
            // Find min and max values for each band
            const mins: number[] = []
            const maxs: number[] = []
            for (let band = 0; band < samples; band++) {
              let min = Infinity
              let max = -Infinity
              for (let i = 0; i < data[band].length; i++) {
                const value = data[band][i]
                if (value < min) min = value
                if (value > max) max = value
              }
              mins.push(min)
              maxs.push(max)
            }

            // Convert the raster data to RGB
            for (let i = 0; i < data[0].length; i++) {
              if (samples === 1) {
                // Grayscale image
                const normalizedValue = Math.min(
                  255,
                  Math.max(0, ((data[0][i] - mins[0]) / (maxs[0] - mins[0])) * 255)
                )
                imageData.data[i * 4] = normalizedValue // R
                imageData.data[i * 4 + 1] = normalizedValue // G
                imageData.data[i * 4 + 2] = normalizedValue // B
                imageData.data[i * 4 + 3] = 255 // A
              } else {
                // Multi-band image - normalize each band
                for (let band = 0; band < Math.min(3, samples); band++) {
                  const normalizedValue = Math.min(
                    255,
                    Math.max(0, ((data[band][i] - mins[band]) / (maxs[band] - mins[band])) * 255)
                  )
                  imageData.data[i * 4 + band] = normalizedValue
                }
                imageData.data[i * 4 + 3] = 255 // Alpha channel
              }
            }
          }

          ctx.putImageData(imageData, 0, 0)

          if (map) {
            // Use PNG format for higher quality initial image
            const imageUrl = await processImageForStorage(canvas.toDataURL('image/png'))

            let bounds: L.LatLngBounds
            if (bbox && bbox.length === 4) {
              // Use the geospatial bounds from the TIFF
              const [minX, minY, maxX, maxY] = bbox
              bounds = L.latLngBounds(L.latLng(minY, minX), L.latLng(maxY, maxX))
            } else {
              // Fallback to centered display if no geospatial information
              const center = map.getCenter()
              const pixelsPerLat = 100000
              const latRatio = image.getHeight() / image.getWidth()
              const lngDelta = image.getWidth() / pixelsPerLat
              const latDelta = lngDelta * latRatio

              bounds = L.latLngBounds(
                L.latLng(center.lat - latDelta / 2, center.lng - lngDelta / 2),
                L.latLng(center.lat + latDelta / 2, center.lng + lngDelta / 2)
              )
            }

            // Create and add the image overlay
            const overlay = L.imageOverlay(imageUrl, bounds)

            // Only add to map if this is the first file or last file
            if (i === files.length - 1) {
              overlay.addTo(map)
            }

            // Create thumbnail
            const thumbnail = await createThumbnail(imageUrl)

            // Get image metadata and GSD
            const metadata = {
              width: image.getWidth(),
              height: image.getHeight(),
              samplesPerPixel: image.getSamplesPerPixel(),
              bitsPerSample: image.getBitsPerSample(),
              photometric: fileDirectory.PhotometricInterpretation,
            }

            // Generate a unique ID for this layer
            const layerId = `file-${Date.now()}-${i}`

            // Add the file to our session file storage
            fileStorage.addFile(layerId, file)

            // Add to new layers array
            const newLayer: LayerInfo = {
              id: layerId,
              bounds,
              overlay,
              thumbnail,
              // Only make the last file visible if multiple files
              isVisible: i === files.length - 1,
              gsd: gsdInfo.gsd,
              metadata,
              imageUrl: imageUrl,
            }

            // Add to the new layers array
            newLayers.push(newLayer)

            // Save each layer to storage immediately
            try {
              await saveLayer(newLayer)
            } catch (storageError) {
              console.warn('Error storing layer:', storageError)
              // Continue anyway - the layer is in memory even if storage fails
            }

            // Update layers state immediately to ensure it's reflected in the UI
            setLayers((prev) => {
              // Filter out the processing placeholder and add the new layer
              return [...prev.filter((layer) => layer.id !== processingId), newLayer]
            })

            // If in inference mode, automatically run inference on the uploaded layer
            if (activeTool === 'inference' && selectedModelId) {
              setTimeout(() => {
                handleInference(newLayer)
              }, 500) // Small delay to ensure layer is fully processed
            }

            // Remove auto-zoom behavior when loading files
            // We'll only zoom when the user explicitly clicks on a layer
          }
        } catch (error) {
          console.error(`Error processing file ${i + 1} (${file.name}):`, error)
          setErrorMessage(`Error processing file "${file.name}". The file may not be a valid TIFF.`)
          setShowError(true)
          // Clear processing state on error
          if (activeTool === 'inference') {
            setIsProcessingFile(false)
          }
        } finally {
          // Remove the processing placeholder regardless of success/failure
          setLayers((prev) => prev.filter((layer) => layer.id !== processingId))
        }
      }

      if (newLayers.length > 0) {
        // Update imageOverlays state with visible overlays
        setImageOverlays((prev) => [
          ...prev,
          ...newLayers.filter((layer) => layer.isVisible).map((layer) => layer.overlay),
        ])
      }
    },
    [map, allowDragAndDrop, activeTool]
  )

  // Handler for files dropped from the toolbar
  const handleToolbarFilesDrop = useCallback(
    async (files: FileList) => {
      if (activeTool !== 'inference') return

      // Convert FileList to the format expected by handleDrop
      const mockEvent = {
        preventDefault: () => {
          // Mock function for drag event compatibility
        },
        stopPropagation: () => {
          // Mock function for drag event compatibility
        },
        dataTransfer: { files },
      } as React.DragEvent<HTMLDivElement>

      await handleDrop(mockEvent)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTool, handleDrop, selectedModelId]
  )

  // Update handleDragOver to check allowDragAndDrop and active tool
  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!allowDragAndDrop || activeTool !== 'inference') return
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(true)
      setIsMapDragOver(true)
    },
    [allowDragAndDrop, activeTool]
  )

  // Add handleDragEnter to detect when files enter the map area
  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!allowDragAndDrop || activeTool !== 'inference') return
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(true)
      setIsMapDragOver(true)
    },
    [allowDragAndDrop, activeTool]
  )

  // Update handleDragLeave to check allowDragAndDrop and active tool
  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!allowDragAndDrop || activeTool !== 'inference') return
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(false)
      setIsMapDragOver(false)
    },
    [allowDragAndDrop, activeTool]
  )

  // Update handleFetchWMSMap
  const handleFetchWMSMap = () => {
    if (!bboxBounds || !map) {
      setErrorMessage(!bboxBounds ? 'Please draw a bounding box first.' : 'Map is not initialized.')
      setShowError(true)
      return
    }

    if (!isAuthenticated) {
      setErrorMessage('You must be logged in to use this feature.')
      setShowError(true)
      return
    }

    setIsDrawingMode(false)
    setErrorMessage(null)

    // Get and normalize bounds
    const sw = bboxBounds.getSouthWest()
    const ne = bboxBounds.getNorthEast()
    const swLng = (((sw.lng % 360) + 540) % 360) - 180
    const neLng = (((ne.lng % 360) + 540) % 360) - 180
    const bboxString = `${sw.lat},${swLng},${ne.lat},${neLng}`

    fetchWMSMap({
      connect_id: connectId,
      username,
      password,
      bbox: bboxString,
      width: wmsImageWidth,
      height: wmsImageHeight,
    })
      .unwrap()
      .then(async (response: WmsMapResponse) => {
        try {
          // Remove existing overlay
          if (imageOverlays.length > 0) {
            imageOverlays.forEach((overlay) => {
              overlay.remove()
            })
            setImageOverlays([])
          }

          // Create bounds and overlay
          const bounds = L.latLngBounds(L.latLng(sw.lat, swLng), L.latLng(ne.lat, neLng))

          // Process the image for storage
          const processedImageUrl = await processImageForStorage(response.imageUrl)

          const overlay = L.imageOverlay(processedImageUrl, bounds, {
            opacity: 1,
            interactive: true,
            crossOrigin: 'anonymous',
          })

          if (map) {
            overlay.addTo(map)
            setImageOverlays((prev) => [...prev, overlay])

            // Create thumbnail
            const thumbnail = await createThumbnail(processedImageUrl)

            const newLayer: LayerInfo = {
              id: `wms-${Date.now()}`,
              bounds,
              overlay,
              thumbnail,
              isVisible: true,
              gsd: response.gsd,
              metadata: response.metadata,
              imageUrl: processedImageUrl,
              originalFile: undefined,
            }

            console.log('WMS Layer created:', newLayer.id)

            // Update state
            setLayers((prev) => {
              const updatedLayers = [...prev, newLayer]
              return updatedLayers
            })

            // Save to storage
            saveLayer(newLayer)

            // Clear drawing and fit map
            if (drawnItems) drawnItems.clearLayers()
            setBboxBounds(null)

            map.fitBounds(bounds, {
              padding: [50, 50],
              animate: true,
              duration: 0.5,
            })
          }
        } catch (error) {
          const errorMessage = error
            ? (error as { data?: { message?: string } }).data?.message || 'Unknown error occurred'
            : 'Unknown error occurred'
          console.error('Error displaying image:', error)
          setErrorMessage('Failed to display the image on the map.')
          setShowError(true)
          if (map) {
            utilCreateDebugOverlay(map, sw.lat, swLng, ne.lat, neLng, errorMessage)
            setImageOverlays((prev) => [
              ...prev,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              utilCreateDebugOverlay(map, sw.lat, swLng, ne.lat, neLng, errorMessage) as any,
            ])

            map.fitBounds(L.latLngBounds(L.latLng(sw.lat, swLng), L.latLng(ne.lat, neLng)), {
              padding: [50, 50],
              animate: true,
              duration: 0.5,
            })
          }
        }
      })
      .catch((error: unknown) => {
        const errorObj = error as { error?: string }
        console.error('Error fetching WMS map:', error)
        setErrorMessage(errorObj.error || 'Failed to fetch the image.')
        setShowError(true)
        if (map) {
          const errorRectangle = utilCreateDebugOverlay(
            map,
            sw.lat,
            swLng,
            ne.lat,
            neLng,
            errorObj.error || 'Failed to fetch image'
          )
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setImageOverlays((prev) => [...prev, errorRectangle as any])

          map.fitBounds(L.latLngBounds(L.latLng(sw.lat, swLng), L.latLng(ne.lat, neLng)), {
            padding: [50, 50],
            animate: true,
            duration: 0.5,
          })
        }
      })
  }

  // Simple zoom function that matches initial file processing behavior
  const zoomToLayerSimple = (bounds: L.LatLngBounds) => {
    if (map) {
      map.fitBounds(bounds, {
        padding: [50, 50],
        animate: true,
        duration: 0.5,
      })

      // Zoom out one additional level for better viewing
      setTimeout(() => {
        const currentZoom = map.getZoom()
        if (currentZoom !== undefined) {
          map.setZoom(currentZoom - 1, {
            animate: true,
            duration: 0.5,
          })
        }
      }, 100)
    }
  }

  // Update the handleInference function to use the file storage
  const handleInference = useCallback(
    async (layer: LayerInfo) => {
      // Get the file from either the layer or the file storage
      const file = layer.originalFile || fileStorage.getFile(layer.id)

      if (!file || !selectedModelId) {
        setErrorMessage(
          'Cannot run inference: either no model is selected or the original file is not available.'
        )
        setShowError(true)
        return
      }

      setIsInferencing(layer.id)
      console.log(`Running inference on layer ${layer.id} with model ${selectedModelId}`)

      // Remove any existing prediction rectangles
      if (layer.predictionRectangles) {
        layer.predictionRectangles.forEach((rect) => {
          if (map) rect.remove()
        })
      }

      try {
        // Create form data with the original TIFF file
        const formData = new FormData()
        formData.append('files', file)

        // Log form data details for debugging
        console.log('Inference form data:', {
          modelId: selectedModelId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          layerGsd: layer.gsd,
        })

        // Run inference using the RTK Query mutation with the original TIFF file
        const result = await runRealtimeTiffInference({
          id: selectedModelId,
          data: formData,
          threshold: 0.1,
          target_gsd: 1,
          tiff_gsd: layer.gsd || 1,
        }).unwrap()

        console.log('Inference result:', result)
        setErrorMessage(null)

        // Process the predictions if they exist
        if (result && Array.isArray(result) && result.length > 0) {
          const predictionResults = result as RealTimeInferenceResult[]
          const predictions = predictionResults[0]?.predictions || []

          console.log(`Found ${predictions.length} predictions`)

          if (predictions.length > 0) {
            // Draw the predictions on the map using our updated function
            const updatedLayer = handleDrawPredictions(layer, predictions)

            // Update the layer in state with new predictions
            setLayers((prev) => prev.map((l) => (l.id === layer.id ? updatedLayer : l)))

            // Also update in storage
            saveLayer(updatedLayer)

            // Show the predictions panel (always for inference mode, conditionally for manual inference)
            setShowPredictionsPanel(true)

            // Zoom to the layer if in inference mode for better visibility
            if (activeTool === 'inference') {
              setTimeout(() => {
                zoomToLayerSimple(layer.bounds)
              }, 200)
            }
          } else {
            setErrorMessage('No objects detected in this image.')
            setShowError(true)
          }
        } else {
          setErrorMessage('No inference results returned from the server.')
          setShowError(true)
        }
      } catch (error) {
        console.error('Error running inference:', error)
        const errorMsg = error
          ? (error as { data?: { message?: string } }).data?.message ||
            (error as Error).message ||
            'An unknown error occurred'
          : 'Failed to run inference'

        setErrorMessage(`Inference failed: ${errorMsg}`)
        setShowError(true)
      } finally {
        setIsInferencing(null)
        setIsProcessingFile(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedModelId, runRealtimeTiffInference, map, activeTool, zoomToLayerSimple]
  )

  // Helper function to handle drawing predictions on a layer
  const handleDrawPredictions = (
    layer: LayerInfo,
    predictions: PredictionMetadata[]
  ): LayerInfo => {
    if (!map || !layer.metadata || !layer.overlay) {
      console.error('Cannot draw predictions: missing map, metadata, or overlay')
      return layer
    }

    // Use the utility function to create prediction rectangles
    const predictionRectangles = utilDrawPredictions(
      map,
      {
        id: layer.id,
        bounds: layer.bounds,
        metadata: layer.metadata,
        overlay: layer.overlay,
        isVisible: layer.isVisible,
      },
      predictions
    )

    // Return updated layer with predictions and rectangles
    return {
      ...layer,
      predictions,
      predictionRectangles,
    }
  }

  // Function to handle drawing rectangle
  const handleDrawRectangle = () => {
    if (!map) return

    if (isDrawingMode) {
      // If already in drawing mode, cancel it
      if (drawControl) {
        drawControl.disable()
        setDrawControl(null)
      }
      setIsDrawingMode(false)
      return
    }

    // Clear existing drawings
    if (drawnItems) {
      drawnItems.clearLayers()
    }
    setBboxBounds(null)

    // Create and enable new draw control
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newDrawControl = new (L.Draw as any).Rectangle(map, {
      shapeOptions: {
        color: '#3388ff',
        weight: 2,
        opacity: 0.5,
        fillOpacity: 0.2,
      },
    })

    setDrawControl(newDrawControl)
    setIsDrawingMode(true)
    newDrawControl.enable()
  }

  // Update the handleWmsSignIn function
  const handleWmsSignIn = () => {
    // Clear any previous error messages
    setErrorMessage(null)

    // Check if user is authenticated to the app
    if (!isAuthenticated) {
      setErrorMessage('You must be logged in to use this feature. Please log in and try again.')
      setShowError(true)
      return
    }

    fetchWMSCapabilities({
      connect_id: connectId,
      username: username,
      password: password,
    })
      .unwrap()
      .then((response: WmsCapabilitiesResponse) => {
        setIsWmsAuthenticated(true)
        setErrorMessage(null)
        setShowGEGDPanel(false) // Close the panel after successful authentication
      })
      .catch((error: unknown) => {
        console.error('Error fetching WMS capabilities:', error)
        setIsWmsAuthenticated(false)

        // Set a user-friendly error message based on the error
        const err = error as { status?: number | string; data?: string }
        if (err.status === 401) {
          setErrorMessage('Authentication failed. Please check your credentials.')
        } else if (err.status === 404) {
          setErrorMessage(
            'The WMS capabilities endpoint was not found. Please check the API configuration.'
          )
        } else if (err.status === 'FETCH_ERROR') {
          setErrorMessage('Network error. Please check your connection and try again.')
        } else {
          setErrorMessage(`Error: ${err.data || 'Unknown error occurred'}`)
        }
        setShowError(true)
      })
  }

  // Update the handleWmsSignOut function
  const handleWmsSignOut = () => {
    setIsWmsAuthenticated(false)
    setShowGEGDPanel(false)
    setShowDrawPanel(false)
    setBboxBounds(null)
    if (drawnItems) {
      drawnItems.clearLayers()
    }
    if (drawControl) {
      drawControl.disable()
      setDrawControl(null)
    }
    setIsDrawingMode(false)
  }

  // Add useEffect to ensure all visible layers are properly displayed on the map
  useEffect(() => {
    if (map) {
      // Make sure all visible layers are on the map
      layers.forEach((layer) => {
        if (layer.isVisible) {
          // Check if the overlay is already on the map
          const isAddedToMap = map.hasLayer(layer.overlay)
          if (!isAddedToMap) {
            layer.overlay.addTo(map)

            // Also add any prediction rectangles if they exist
            if (layer.predictionRectangles) {
              layer.predictionRectangles.forEach((rect) => {
                const isRectOnMap = map.hasLayer(rect)
                if (!isRectOnMap) {
                  rect.addTo(map)
                }
              })
            }
          }
        }
      })
    }
  }, [map, layers])

  // Function to zoom to a specific prediction
  const zoomToPrediction = (layerId: string, prediction: PredictionMetadata) => {
    if (!map) return

    const layer = layers.find((l) => l.id === layerId)
    if (!layer || !layer.metadata) return

    console.log(
      `Zooming to prediction in layer ${layerId}, class: ${prediction.class_name}, index: ${prediction.class_index}`
    )

    // Find the exact prediction rectangle by matching both class and position
    // const { xmin, ymin, xmax, ymax } = prediction.bounding_boxes
    const predictionBounds = createBoundsFromPrediction(layer, prediction)

    // Try to find the corresponding rectangle by comparing bounds
    const predictionRectangle = layer.predictionRectangles?.find((rect) => {
      // Check if class names match
      const classMatch = rect.options.className?.includes(prediction.class_name)
      if (!classMatch) return false

      // Get the rectangle bounds and compare corners within a small tolerance
      const rectBounds = rect.getBounds()
      const rectSW = rectBounds.getSouthWest()
      const rectNE = rectBounds.getNorthEast()
      const predSW = predictionBounds.getSouthWest()
      const predNE = predictionBounds.getNorthEast()

      // Use a small tolerance for coordinate comparison (to handle floating point differences)
      const tolerance = 0.0000001
      const swMatch =
        Math.abs(rectSW.lat - predSW.lat) < tolerance &&
        Math.abs(rectSW.lng - predSW.lng) < tolerance
      const neMatch =
        Math.abs(rectNE.lat - predNE.lat) < tolerance &&
        Math.abs(rectNE.lng - predNE.lng) < tolerance

      return swMatch && neMatch
    })

    if (predictionRectangle) {
      console.log('Found matching prediction rectangle')
      // Get the bounds of the prediction rectangle
      const bounds = predictionRectangle.getBounds()

      // Fit map to the prediction with some padding
      map.fitBounds(bounds, {
        padding: [50, 50],
        animate: true,
        duration: 0.5,
        maxZoom: 18, // Limit how far we can zoom in
      })

      // Highlight the rectangle temporarily
      const originalStyle = {
        color: predictionRectangle.options.color,
        weight: predictionRectangle.options.weight,
        opacity: predictionRectangle.options.opacity,
        fillOpacity: predictionRectangle.options.fillOpacity,
      }

      // Apply highlight style
      predictionRectangle.setStyle({
        color: '#FFFFFF',
        weight: 3,
        opacity: 1,
        fillOpacity: 0.4,
      })

      // Reset style after a short delay
      setTimeout(() => {
        predictionRectangle.setStyle(originalStyle)
      }, 1500)
    } else {
      console.log('No matching rectangle found, creating temporary one')
      // Use the calculated bounds
      map.fitBounds(predictionBounds, {
        padding: [50, 50],
        animate: true,
        duration: 0.5,
        maxZoom: 18,
      })

      // Create a temporary highlight rectangle
      const tempRect = L.rectangle(predictionBounds, {
        color: '#FFFFFF',
        weight: 3,
        opacity: 1,
        fillOpacity: 0.4,
        fillColor: getClassColor(prediction.class_name),
      }).addTo(map)

      // Remove after delay
      setTimeout(() => {
        tempRect.remove()
      }, 1500)
    }
  }

  // Helper function to create bounds from a prediction
  const createBoundsFromPrediction = (
    layer: LayerInfo,
    prediction: PredictionMetadata
  ): L.LatLngBounds => {
    if (!prediction.bounding_boxes) {
      console.error('No bounding boxes found for prediction')
      return L.latLngBounds(L.latLng(0, 0), L.latLng(0, 0))
    }

    const { xmin, ymin, xmax, ymax } = prediction.bounding_boxes

    // Get image dimensions
    const imageWidth = layer.metadata?.width || 1
    const imageHeight = layer.metadata?.height || 1

    // Get the bounds of the layer
    const layerBounds = layer.bounds
    const southWest = layerBounds.getSouthWest()
    const northEast = layerBounds.getNorthEast()

    const lngDiff = northEast.lng - southWest.lng
    const latDiff = northEast.lat - southWest.lat

    // Calculate the proportional position in the image
    const proportionalXmin = xmin ? xmin / imageWidth : 0
    const proportionalXmax = xmax ? xmax / imageWidth : 0
    const proportionalYmin = ymin ? 1 - ymin / imageHeight : 0
    const proportionalYmax = ymax ? 1 - ymax / imageHeight : 0

    // Convert to map coordinates
    const rectSouthWest = L.latLng(
      southWest.lat + latDiff * proportionalYmax,
      southWest.lng + lngDiff * proportionalXmin
    )

    const rectNorthEast = L.latLng(
      southWest.lat + latDiff * proportionalYmin,
      southWest.lng + lngDiff * proportionalXmax
    )

    // Create and return bounds
    return L.latLngBounds(rectSouthWest, rectNorthEast)
  }

  // Helper function to get class color
  const getClassColor = (className: string): string => {
    const colorMap: Record<string, string> = {
      combatant: '#FF5733',
      cargo: '#33FF57',
      tanker: '#3357FF',
      fishing: '#FF33A8',
      passenger: '#33FFF5',
      tug: '#F533FF',
      default: '#FFFF33',
    }
    return colorMap[className] || colorMap['default']
  }

  return (
    <ContentContainer>
      <MapContainerComponent
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        isDragging={isDragging}
        isDimmed={isMapDragOver}
        isLoading={isProcessingFile || !!isInferencing}
      >
        {/* Map Toolbar */}
        <MapToolbar
          activeTool={activeTool}
          onToolSelect={handleToolSelect}
          onFilesDrop={handleToolbarFilesDrop}
          isDragActive={isDragging}
          hasSelectedModel={!!selectedModelId}
        />

        {/* WMS Action Button */}
        {bboxBounds && isWmsAuthenticated && !showDrawPanel && (
          <WmsActionButton onClick={handleFetchWMSMap} isLoading={isLoadingMap} />
        )}

        {/* Inference Panel - only shown when inference tool is active */}
        {activeTool === 'inference' && (
          <InferenceToolMapPanel
            layers={layers}
            hidden={!isLayersPanelVisible}
            toggleLayerVisibility={toggleLayerVisibility}
            removeLayer={removeLayer}
            zoomToLayer={zoomToLayerSimple}
            handleInference={handleInference}
            isInferencing={isInferencing}
            selectedModelId={selectedModelId}
            onTogglePanel={toggleLayersPanel}
            activeTool={activeTool}
          />
        )}

        {/* Collapsed Inference Icon (when panel is hidden and in inference mode) */}
        {!isLayersPanelVisible && activeTool === 'inference' && (
          <div
            style={{
              position: 'absolute',
              top: '80px',
              left: '10px',
              zIndex: 1000,
              backgroundColor: 'rgba(26, 26, 26, 0.9)',
              color: 'white',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
            }}
            onClick={toggleLayersPanel}
          >
            <i className="bi bi-cpu" style={{ fontSize: '18px' }}></i>
          </div>
        )}

        {/* WMS Panel */}
        <WmsPanel
          showGEGDPanel={showGEGDPanel}
          isWmsAuthenticated={isWmsAuthenticated}
          connectId={connectId}
          setConnectId={setConnectId}
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
          handleWmsSignIn={handleWmsSignIn}
          handleWmsSignOut={handleWmsSignOut}
          isLoadingCapabilities={isLoadingCapabilities}
          errorMessage={errorMessage}
          setShowGEGDPanel={setShowGEGDPanel}
        />

        {/* Draw Panel */}
        <DrawPanel
          showDrawPanel={showDrawPanel}
          isDrawingMode={isDrawingMode}
          handleDrawRectangle={handleDrawRectangle}
          isLoadingMap={isLoadingMap}
          selectedResolution={selectedResolution.toString()}
          setSelectedResolution={(value) =>
            setSelectedResolution(value as keyof typeof PRESET_RESOLUTIONS)
          }
          resolutions={PRESET_RESOLUTIONS}
          bboxBounds={bboxBounds}
          handleFetchWMSMap={handleFetchWMSMap}
          errorMessage={errorMessage}
          setShowDrawPanel={setShowDrawPanel}
        />

        {/* Predictions Panel - only show when inference tool is active and there are uploaded files */}
        {activeTool === 'inference' && layers.length > 0 && (
          <PredictionsPanel
            layers={layers}
            hidden={!showPredictionsPanel}
            zoomToPrediction={zoomToPrediction}
            onTogglePanel={togglePredictionsPanel}
          />
        )}

        {/* Add Predictions Panel Toggle Button (only visible when inference tool active, files uploaded and predictions exist) */}
        {activeTool === 'inference' &&
          layers.length > 0 &&
          layers.some(
            (layer) => layer.isVisible && layer.predictions && layer.predictions.length > 0
          ) &&
          !showPredictionsPanel && (
            <div
              style={{
                position: 'absolute',
                top: '80px',
                right: '10px',
                zIndex: 1000,
                backgroundColor: 'rgba(26, 26, 26, 0.9)',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
              }}
              onClick={togglePredictionsPanel}
              title="Show Predictions"
            >
              <i className="bi bi-binoculars" style={{ fontSize: '18px' }}></i>
            </div>
          )}
      </MapContainerComponent>

      {/* Error Dialog */}
      {showError && (
        <ErrorDialog
          show={showError}
          onClose={() => setShowError(false)}
          message={errorMessage || 'An error occurred. Please try again later.'}
        />
      )}
    </ContentContainer>
  )
}

export default MapComponent
